import React, { useEffect, useState } from "react";
import { Star, ExternalLink, Send, ThumbsUp, Clock, Lightbulb } from "lucide-react";
import api from "@/lib/api";
import { useCockpit } from "./CockpitLayout";
import { Card, BtnPrimary, SectionLabel } from "./ui";
import { Spinner } from "@/components/common";
import { useI18n } from "@/context/I18nContext";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return iso; } };

export default function CReviews() {
  const { accent, clientId, openAi } = useCockpit();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const qs = clientId ? `?client_id=${clientId}` : "";

  useEffect(() => { api.get(`/cockpit/reviews${qs}`).then((r) => setData(r.data)); }, [clientId]); // eslint-disable-line
  if (!data) return <Spinner />;

  const stars = (n) => "★".repeat(n || 0) + "☆".repeat(Math.max(0, 5 - (n || 0)));

  return (
    <div className="fade-in space-y-5" data-testid="cockpit-reviews">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[26px] lg:text-[34px] leading-tight font-black tracking-tight">{t("Bewertungen")}</h1>
          <p className="text-sm text-gray-400 mt-1.5 max-w-2xl">{t("So sammeln wir gemeinsam echte Bewertungen für mehr Vertrauen und mehr Anfragen.")}</p>
        </div>
        {data.google_review_link && (
          <a href={data.google_review_link} target="_blank" rel="noreferrer" data-testid="reviews-google-link"
            className="text-black font-bold py-2.5 px-4 rounded-lg text-sm flex items-center gap-2 w-full sm:w-auto justify-center" style={{ background: accent }}>
            <Star className="w-4 h-4" /> {t("Jetzt bewerten")} <ExternalLink className="w-3.5 h-3.5" />
          </a>
        )}
      </div>

      {/* Warum wichtig */}
      <Card className="p-5" data-testid="reviews-why">
        <div className="flex items-center gap-2 mb-2"><Lightbulb className="w-5 h-5" style={{ color: accent }} /><span className="font-bold">{t("Warum Bewertungen wichtig sind")}</span></div>
        <p className="text-sm text-gray-300">{data.why}</p>
      </Card>

      {!data.connected ? (
        <Card className="p-8 text-center" data-testid="reviews-empty">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: `${accent}1A` }}><Star className="w-6 h-6" style={{ color: accent }} /></div>
          <h3 className="font-bold text-lg">{t("Noch nicht verbunden")}</h3>
          <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">{t("Es sind noch keine echten Bewertungsdaten vorhanden. KEWAG hinterlegt den Bewertungslink und plant erste Bewertungsaktionen.")}</p>
          <BtnPrimary accent={accent} onClick={openAi} data-testid="reviews-empty-ask" className="mt-4 mx-auto"><Send className="w-4 h-4" /> {t("Frage stellen")}</BtnPrimary>
        </Card>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl p-4 border bg-[#15181e] border-white/5" data-testid="reviews-stat-goal">
              <div className="text-3xl font-black leading-none">{data.monthly_goal || "—"}</div>
              <div className="text-xs text-gray-500 mt-1.5">{t("Ziel pro Monat")}</div>
            </div>
            <div className="rounded-2xl p-4 border bg-[#15181e] border-white/5" data-testid="reviews-stat-received">
              <div className="text-3xl font-black leading-none" style={{ color: accent }}>{data.received_count}</div>
              <div className="text-xs text-gray-500 mt-1.5">{t("Erhaltene Bewertungen")}</div>
            </div>
            <div className="rounded-2xl p-4 border bg-[#15181e] border-white/5" data-testid="reviews-stat-open">
              <div className="text-3xl font-black leading-none text-amber-300">{data.open_actions.length}</div>
              <div className="text-xs text-gray-500 mt-1.5">{t("Offene Aktionen")}</div>
            </div>
          </div>

          {/* Nächste empfohlene Aktion */}
          {data.next_recommended && (
            <Card className="p-5" data-testid="reviews-next">
              <div className="flex items-center gap-2 mb-1"><ThumbsUp className="w-5 h-5" style={{ color: accent }} /><span className="font-bold">{t("Nächste empfohlene Aktion")}</span></div>
              <p className="text-sm text-gray-300">{data.next_recommended}</p>
            </Card>
          )}

          {/* Erhaltene Bewertungen */}
          {data.received_reviews.length > 0 && (
            <div>
              <SectionLabel>{t("Erhaltene Bewertungen")}</SectionLabel>
              <div className="grid sm:grid-cols-2 gap-3 mt-2">
                {data.received_reviews.map((r) => (
                  <Card key={r.id} className="p-4" data-testid={`review-received-${r.id}`}>
                    <div className="text-amber-300 text-lg tracking-widest">{stars(r.rating)}</div>
                    {r.review_text && <p className="text-sm text-gray-300 mt-1.5 italic">«{r.review_text}»</p>}
                    {r.received_at && <p className="text-xs text-gray-500 mt-1">{fmtDate(r.received_at)}</p>}
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Offene Aktionen */}
          {data.open_actions.length > 0 && (
            <div>
              <SectionLabel>{t("Offene Bewertungsaktionen")}</SectionLabel>
              <Card className="divide-y divide-white/5 mt-2">
                {data.open_actions.map((a) => (
                  <div key={a.id} className="p-4 flex items-center justify-between gap-3" data-testid={`review-open-${a.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{a.client_hint || t("Bewertungsanfrage")}</div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2"><Clock className="w-3 h-3" /> {a.status_label} · {a.channel_label}</div>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 shrink-0">{a.status_label}</span>
                  </div>
                ))}
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}
